import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { alertsPagePath } from '../utils/alertNavigation';
import api from '../services/api';
import logger from '../utils/logger';

interface NotificationItem {
  id: string;
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'critical';
  alertId?: number; // Backend alert ID for acknowledge
  timestamp: Date;
}

interface NotificationPopupProps {
  onClose?: (id: string) => void;
}

// Notification sound (base64 encoded short beep)
const ALERT_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2AgoGAgoeJi4qHhoOAgn59fH1+gIKDhIWGh4eHhoeHh4aFhIOCgYB/fn18e3t7fH1+f4CCg4SFhoaHh4eHhoeGhYSDgoGAf359fHt7e3x9fn+AgoOEhYaGh4eHh4aGhYSDgoGAf359fHt7e3x9fn+AgoOEhYaGh4eHh4aGhYSDgoGAf359fHt7e3x9fn+AgoOEhYaGh4eHh4aGhYSDgoGAf359fHt7e3x9fn+AgoOEhYaGh4eHh4aGhYSDgoGA';

let notificationAudio: HTMLAudioElement | null = null;

// Initialize audio on first user interaction
const initAudio = () => {
  if (!notificationAudio) {
    notificationAudio = new Audio(ALERT_SOUND);
    notificationAudio.volume = 0.5;
  }
};

// Play notification sound
const playNotificationSound = (severity: string) => {
  try {
    initAudio();
    if (notificationAudio) {
      notificationAudio.currentTime = 0;
      // Play multiple times for critical alerts
      if (severity === 'critical') {
        notificationAudio.play();
        setTimeout(() => notificationAudio?.play(), 300);
        setTimeout(() => notificationAudio?.play(), 600);
      } else {
        notificationAudio.play();
      }
    }
  } catch (error) {
    logger.warn('Could not play notification sound:', error);
  }
};

// Global notification queue
let addNotificationFn: ((notification: Omit<NotificationItem, 'id' | 'timestamp'>) => void) | null = null;

// Export function to show notifications from anywhere
export const showNotificationPopup = (
  title: string,
  message: string,
  severity: 'info' | 'warning' | 'critical' = 'info',
  alertId?: number
) => {
  if (addNotificationFn) {
    addNotificationFn({ title, message, severity, alertId });
  }
};

const NotificationPopup: React.FC<NotificationPopupProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const addNotification = useCallback((notification: Omit<NotificationItem, 'id' | 'timestamp'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNotification: NotificationItem = {
      ...notification,
      id,
      timestamp: new Date()
    };

    setNotifications(prev => [newNotification, ...prev]);
    playNotificationSound(notification.severity);

    // Auto-dismiss after 10 seconds for info/warning, 30 seconds for critical
    const timeout = notification.severity === 'critical' ? 30000 : 10000;
    setTimeout(() => {
      removeNotification(id);
    }, timeout);
  }, []);

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    onClose?.(id);
  };

  const acknowledgeNotification = async (notification: NotificationItem) => {
    // If this notification is linked to a backend alert, acknowledge with defaults
    if (notification.alertId) {
      try {
        await api.alerts.acknowledge(notification.alertId, {
          corrective_action: 'No action required — transient event',
          product_disposition: 'No impact — product within spec',
          notes: 'Quick acknowledged via notification popup',
        });
        logger.log(`Alert ${notification.alertId} acknowledged via popup`);
      } catch (error) {
        logger.error('Failed to acknowledge alert via popup:', error);
      }
    }
    removeNotification(notification.id);
  };

  // Register the add function globally
  useEffect(() => {
    addNotificationFn = addNotification;
    return () => {
      addNotificationFn = null;
    };
  }, [addNotification]);

  // Initialize audio on component mount (needs user interaction first)
  useEffect(() => {
    const handleInteraction = () => {
      initAudio();
      document.removeEventListener('click', handleInteraction);
    };
    document.addEventListener('click', handleInteraction);
    return () => document.removeEventListener('click', handleInteraction);
  }, []);

  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'critical':
        return {
          container: 'bg-red-600 border-red-700',
          icon: '🚨',
          title: 'text-white',
          message: 'text-red-100'
        };
      case 'warning':
        return {
          container: 'bg-yellow-500 border-yellow-600',
          icon: '⚠️',
          title: 'text-white',
          message: 'text-yellow-100'
        };
      default:
        return {
          container: 'bg-blue-600 border-blue-700',
          icon: 'ℹ️',
          title: 'text-white',
          message: 'text-blue-100'
        };
    }
  };

  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-4 left-4 right-4 sm:left-auto z-[100] space-y-3 max-w-md w-auto sm:w-full pointer-events-none">
      {notifications.map((notification) => {
        const styles = getSeverityStyles(notification.severity);
        return (
          <div
            key={notification.id}
            className={`${styles.container} rounded-lg shadow-2xl border-2 p-4 pointer-events-auto transform transition-all duration-300 animate-slide-in`}
            role="alert"
          >
            <div className="flex items-start">
              <span className="text-2xl mr-3 flex-shrink-0">{styles.icon}</span>
              <div className="flex-1 min-w-0">
                <h4 className={`font-bold ${styles.title} text-lg`}>
                  {notification.title}
                </h4>
                <p className={`${styles.message} mt-1 text-sm`}>
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => removeNotification(notification.id)}
                className="ml-3 text-white/80 hover:text-white transition-colors flex-shrink-0"
                aria-label="Close notification"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Progress bar for auto-dismiss */}
            <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white/60 rounded-full animate-shrink"
                style={{
                  animationDuration: notification.severity === 'critical' ? '30s' : '10s'
                }}
              />
            </div>

            {/* Action buttons for critical alerts */}
            {notification.severity === 'critical' && (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => acknowledgeNotification(notification)}
                  className="flex-1 px-3 py-2 bg-white text-red-600 font-semibold rounded-lg hover:bg-red-50 transition-colors text-sm"
                >
                  Acknowledge
                </button>
                <button
                  onClick={() => {
                    removeNotification(notification.id);
                    // Open the alert itself when we know which one it is
                    navigate(notification.alertId ? alertsPagePath(notification.alertId) : '/alerts');
                  }}
                  className="flex-1 px-3 py-2 bg-red-700 text-white font-semibold rounded-lg hover:bg-red-800 transition-colors text-sm"
                >
                  View Details
                </button>
              </div>
            )}
          </div>
        );
      })}

      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
        .animate-shrink {
          animation: shrink linear forwards;
        }
      `}</style>
    </div>
  );
};

export default NotificationPopup;
